import { useState } from "react";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/pages/components/input-password";
import BackNavigator from "./components/back-navigation";
import { resetPasswordSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { useForm } from "@tanstack/react-form";
import type { IAccountResetResponse } from "@/hooks/use-account-reset";
import {
  usePasswordReset,
  useVerifyForgotPasswordOTP,
} from "@/hooks/use-account-reset";
import { useNavigate, useLocation } from "react-router-dom";
import { useGroupProvider } from "@/context/group-provider";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import { ErrorNotificationType } from "./components/error-notification";
import ScreenRouteNames from "@/constants/screen-route-names";
import InfoToolTip from "./components/info-tooltip";
import { PASSWORD_REQUIREMENTS_MESSAGE } from "@/constants";
import IDENTIFIERS from "@/constants/test_id";

export default function ResetPassword() {
  const resetQuery = useVerifyForgotPasswordOTP();
  const resendOtpQuery = usePasswordReset();
  const location = useLocation();
  const navigate = useNavigate();
  const { groupData } = useGroupProvider();
  const [signature, setSignature] = useState(location.state?.signature);

  const handleResendOtp = () => {
    resendOtpQuery.mutate(
      {
        username: location.state?.userInfo?.username,
        groupId: groupData!.group_id,
        loginGroupId: groupData!.entered_id,
        divisionId: groupData!.division_id,
      },
      {
        onSuccess: (data: IAccountResetResponse) => {
          if (!data.success) {
            // emitEvent(EventConstants.ADD_ERROR, {
            //   message: data.message,
            //   type: ErrorNotificationType.ERROR,
            // });
            return;
          } else {
            if (data.signature) {
              setSignature(data.signature);
            }
            emitEvent(EventConstants.ADD_ERROR, {
              message: data.message,
              type: ErrorNotificationType.INFO,
            });
          }
        },
      },
    );
  };

  const form = useForm({
    defaultValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: ({ value }) => {
      const resetInfo = {
        username: location.state?.userInfo?.username,
        enteredOTP: value.otp,
        password: value.password,
        groupId: location.state?.userInfo?.groupId,
        loginGroupId: location.state?.userInfo?.loginGroupId,
        divisionId: location.state?.userInfo?.divisionId,
        signature,
      };
      resetQuery.mutate(resetInfo, {
        onSuccess: (data: IAccountResetResponse) => {
          if (!data.success) return;
          navigate(`/${ScreenRouteNames.LOGIN.path}`, { replace: true });
        },
      });
    },
  });

  return (
    <main className="flex h-full flex-col items-center justify-center space-y-10 px-8">
      <BackNavigator
        title="Reset Password"
        left={
          <Button
            variant="ghost"
            aria-label="Go back"
            className="left-0 h-auto w-fit bg-transparent p-0 hover:bg-transparent hover:shadow-none focus:bg-transparent focus:shadow-none active:bg-transparent"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/nav-error");
              }
            }}
            data-testid={IDENTIFIERS.VERIFICATION.BACK_BUTTON}
          >
            <ChevronLeft size={20} />
          </Button>
        }
      />
      <div className="flex h-full items-center justify-center bg-white">
        <div className="w-full max-w-sm p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="flex flex-col">
              <Label className="py-4 text-center text-lg font-bold">
                Check your email
              </Label>
              <Label className="py-4 text-center text-xs text-gray-500">
                Please enter the six digit verification code we sent to your
                registered email.
              </Label>
            </div>

            <div className="space-y-2">
              <form.Field
                name="otp"
                validators={{
                  onChange: ({ value }) => {
                    const result =
                      resetPasswordSchema.shape.otp.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex w-full flex-col items-center justify-center space-y-2">
                    {/* Hidden field mirrors the real OTP so form context can see it.
                        Because "otp" is in SENSITIVE_FORM_KEYS, logger will log it as "***". */}
                    <input
                      type="hidden"
                      id="otp"
                      name="otp"
                      value={field.state.value}
                    />
                    <InputOTP
                      maxLength={6}
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      onBlur={() => field.handleBlur()}
                      data-testid={IDENTIFIERS.VERIFICATION.OTP_INPUT}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                    {field.state.meta.errors[0] && (
                      <em className="w-full text-center text-xs text-red-500">
                        {field.state.meta.errors[0]}
                      </em>
                    )}
                  </div>
                )}
              </form.Field>
            </div>

            {/* Resend OTP */}
            <div className="flex flex-row items-center justify-center gap-3">
              <span className="py-4 text-center text-[10px] text-gray-500">
                Didn&apos;t get the email?
              </span>
              <Button
                type="button"
                variant="link"
                className="p-0 text-xs"
                onClick={handleResendOtp}
                data-testid={IDENTIFIERS.VERIFICATION.RESEND_OTP_BUTTON}
              >
                Resend OTP
              </Button>
            </div>

            {/* New Password */}
            <div className="space-y-4">
              <div className="space-y-1">
                <form.Field
                  name="password"
                  validators={{
                    onBlur: ({ value }) => {
                      const result =
                        resetPasswordSchema.shape.password.safeParse(value);
                      return result.success
                        ? undefined
                        : result.error.errors[0].message;
                    },
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <div className="flex flex-row items-center space-x-1">
                        <Label htmlFor="password">New Password</Label>
                        <InfoToolTip info={PASSWORD_REQUIREMENTS_MESSAGE} />
                      </div>
                      <PasswordInput
                        id="password"
                        placeholder="New Password"
                        value={field.state.value}
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                        data-testid={
                          IDENTIFIERS.VERIFICATION.NEW_PASSWORD_INPUT
                        }
                      />
                    </div>
                  )}
                </form.Field>
              </div>
              <div className="space-y-1">
                <form.Field
                  name="confirmPassword"
                  validators={{
                    onBlur: ({ value, fieldApi }) => {
                      const result =
                        resetPasswordSchema.shape.confirmPassword.safeParse(
                          value,
                        );
                      if (!result.success) {
                        return result.error.issues[0]?.message;
                      }
                      const password = fieldApi.form.getFieldValue("password");
                      if (password && value !== password) {
                        return "Passwords do not match";
                      }
                      return undefined;
                    },
                    onChangeListenTo: ["password"],
                  }}
                >
                  {(field) => (
                    <div className="space-y-1">
                      <Label htmlFor="confirmPassword">
                        Confirm New Password
                      </Label>
                      <PasswordInput
                        id="confirmPassword"
                        value={field.state.value}
                        placeholder="New Password Again"
                        onChange={(newValue) => field.handleChange(newValue)}
                        onBlur={() => field.handleBlur()}
                        error={field.state.meta.errors[0]}
                        data-testid={
                          IDENTIFIERS.VERIFICATION.CONFIRM_NEW_PASSWORD_INPUT
                        }
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </div>

            {/* Confirm Button */}
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.values.otp,
                state.values.password,
                state.values.confirmPassword,
              ]}
            >
              {([canSubmit, otp, password, confirmPassword]) => {
                // 3.3.1 Error Identification — A: names the specific empty
                // field(s) rather than a static list.
                const requirement =
                  describeMissingFields([
                    { label: "verification code", value: otp },
                    { label: "new password", value: password },
                    { label: "confirm new password", value: confirmPassword },
                  ]) || "Disabled. Fix the errors above to continue.";
                return (
                  <>
                    <Button
                      type="submit"
                      size="lg"
                      variant="default"
                      className={cn(
                        "mt-4 w-full bg-primary hover:bg-primary-selected",
                        !canSubmit && "opacity-50",
                      )}
                      aria-disabled={!canSubmit}
                      aria-describedby={
                        !canSubmit ? "confirm-requirement" : undefined
                      }
                      data-testid={IDENTIFIERS.VERIFICATION.CONFIRM_BUTTON}
                    >
                      Confirm
                    </Button>
                    {!canSubmit && (
                      <p id="confirm-requirement" className="sr-only">
                        {requirement}
                      </p>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          </form>
        </div>
      </div>
    </main>
  );
}
